<template>
  <Section
    :id="id"
    :header="t('shipments.shipments', 2)"
    class="route-builder-shipment-section phase-1"
  >
    <template v-if="!!shipments.length" #headerRight>
      <RygenButton
        mode="tertiary"
        :label="t('common.clearParam', { param: t('shipments.shipments', 2) })"
        :severity="Severity.DANGER"
        @click="onClear"
      />
    </template>

    <template v-if="!shipments.length" #default>
      <figure aria-describedby="add-shipment-instructions">
        <!-- TODO: Touch this up -->
        <img
          src="@/assets/img/route_builder_empty_shipments.svg"
          alt=""
          width="187.5px"
          height="225px"
        />

        <figcaption>
          {{ t('common.noParamYet', { param: t('shipments.shipments', 2) }) }}
        </figcaption>

        <p id="add-shipment-instructions">
          {{ t('routeBuilder.startWithShipment') }}
        </p>

        <RygenButton
          mode="primary"
          :icon="PrimeIcons.PLUS"
          :label="t('common.addParam', { param: t('shipments.shipments') })"
          @click="emit('add-shipment')"
        />
      </figure>
    </template>
    <template v-else #default>
      <SkeletonDataTable
        v-model:selection="selection"
        v-model:expanded-rows="expandedRows"
        :value="shipments"
        data-key="identifier"
        :first="0"
        :rows="10"
        :number-of-rows="5"
        :total-records="shipments.length"
        class="elevated"
        :collapsed-row-icon="PrimeIcons.CHEVRON_DOWN"
        :expanded-row-icon="PrimeIcons.CHEVRON_UP"
        :pt="{
          table: {
            'aria-label': t('routeBuilder.proposedShipments', 2),
          },
          bodyRow: ({ instance: { rowData } }: DataTablePT) => ({
            'aria-labelledby': `shipments-row-header-${rowData.identifier}`,
          }),
        }"
        responsive-layout="scroll"
        scroll-height="flex"
        auto-layout
        lazy
        removable-sort
        scrollable
        @row-expand="onRowExpand($event.data)"
      >
        <Column
          field="identifier"
          :header="t('shipments.shipments')"
          :pt="{
            bodyCell: ({ instance: { rowData } }: ColumnPT) => ({
              id: `shipments-row-header-${rowData.identifier}`,
              role: 'rowheader',
              style: {
                'border-left': `4px solid ${rowData.color}`,
              },
            }),
          }"
        >
          <template #body="{ data, index }: DataTableRow">
            <ProposedShipmentId
              :ref="setRef(index)"
              :proposed-shipment="data"
            />
          </template>
        </Column>

        <Column field="origin" :header="t('orders.origin')">
          <template #body="{ data }: DataTableRow">
            <ProposedShipmentShippingPoint :shipping-point="data.origin" />
          </template>
        </Column>

        <Column field="destination" :header="t('orders.destination')">
          <template #body="{ data }: DataTableRow">
            <ProposedShipmentShippingPoint :shipping-point="data.destination" />
          </template>
        </Column>

        <Column :header="t('lineItems.lineItems', 2)">
          <template #body="{ data }: DataTableRow">
            <LineItemSummarySearchResult
              :line-items="data.lineItems"
              show-linear
            />
          </template>
        </Column>

        <Column
          :header-style="{ width: '3rem', 'min-width': '3rem' }"
          align-frozen="right"
          frozen
        >
          <template #body="{ data }: DataTableRow">
            <ProposedShipmentMenu
              :id="`shipment-menu-${data.identifier}`"
              :proposed-shipment="data"
              :proposed-shipments="shipments"
              @remove="emit('remove-shipment', $event)"
            />
          </template>
        </Column>

        <Column
          :header-style="{ width: '3rem', 'min-width': '3rem' }"
          align-frozen="right"
          expander
          frozen
        />

        <template #expansion="{ data }: DataTableRow">
          <RouteBuilderTableExpandedDetails
            :entity="data"
            :mileage="mileages[data.identifier]?.response"
            @add-stop="emit('add-stop', $event)"
            @rearrange-stops="emit('rearrange-stops', $event)"
            @remove-ship-order="emit('remove-ship-order', $event)"
            @remove-stop="emit('remove-stop', $event)"
          />
        </template>
      </SkeletonDataTable>

      <AddButton
        mode="tertiary"
        class="align-self-start"
        :label="t('shipments.shipments')"
        @click="emit('add-shipment')"
      />
    </template>

    <template v-if="shipments.length" #footer>
      <RygenButton
        mode="secondary"
        :icon="PrimeIcons.SAVE"
        :label="
          t('common.createParam', {
            param: t('shipments.shipments', shipments.length),
          })
        "
        :disabled="disabled"
        @click="emit('submit')"
      />
      <RygenButton
        mode="primary"
        :icon="PrimeIcons.ARROW_RIGHT"
        :label="
          t('common.rateParam', {
            param: t('shipments.shipments', shipments.length),
          })
        "
        :disabled="disabled"
        @click="emit('rate')"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import type { PropType, Ref } from 'vue';
import { nextTick, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  useExpandedRows,
  useScrollAnimations,
} from '@rygen/rygen-ui/composables';
import { Severity } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { Mileages } from '@/composables/routeBuilder';
import {
  RequestAddStopToShipmentPayload,
  ProposedShipment,
  RearrangeStopsPayload,
  RemoveShipmentPayload,
  RemoveShipOrderFromShipmentPayload,
  RemoveStopFromShipmentPayload,
} from '@/model/routeBuilder';

import ProposedShipmentId from '@/components/routeBuilder/proposedShipment/ProposedShipmentId.vue';
import ProposedShipmentMenu from '@/components/routeBuilder/proposedShipment/ProposedShipmentMenu.vue';
import ProposedShipmentShippingPoint from '@/components/routeBuilder/proposedShipment/ProposedShipmentShippingPoint.vue';
import RouteBuilderTableExpandedDetails from '@/components/routeBuilder/RouteBuilderTableExpandedDetails.vue';
import LineItemSummarySearchResult from '@/components/search/LineItemSummarySearchResult.vue';
import AddButton from '@/components/ui/AddButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import SkeletonDataTable from '@rygen/rygen-ui/components/ui/SkeletonDataTable.vue';
import Column from 'primevue/column';

export type DataTableRow = { data: ProposedShipment; index: number };
type ProposedShipmentIdComponent = InstanceType<typeof ProposedShipmentId>;

type DataTablePT = {
  instance: { rowData: ProposedShipment };
};

type ColumnPT = {
  instance: { rowData: ProposedShipment };
};

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipments: {
    type: Array as PropType<ProposedShipment[]>,
    default: () => [],
  },
  mileages: {
    type: Object as PropType<Mileages>,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { id, shipments, mileages, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'add-shipment'): void;
  (event: 'add-stop', payload: RequestAddStopToShipmentPayload): void;
  (event: 'clear'): void;
  (event: 'rate'): void;
  (event: 'rearrange-stops', payload: RearrangeStopsPayload): void;
  (
    event: 'remove-ship-order',
    payload: RemoveShipOrderFromShipmentPayload,
  ): void;
  (event: 'remove-shipment', payload: RemoveShipmentPayload): void;
  (event: 'remove-stop', payload: RemoveStopFromShipmentPayload): void;
  (event: 'submit'): void;
}>();

const { expandedRows } = useExpandedRows(shipments, {
  expandIfSingleItem: true,
});
const { t } = useI18n();
const { scrollIntoView } = useScrollAnimations();

const proposedShipmentIdComponents: Ref<
  Array<ProposedShipmentIdComponent | null>
> = ref([]);
const selection: Ref<ProposedShipment[]> = ref([]);

const setRef = (index: number) => (el: any) => {
  proposedShipmentIdComponents.value[index] = el as ProposedShipmentIdComponent;
};

const onRowExpand = (shipment: ProposedShipment) => {
  nextTick(() => {
    const el = document.querySelector<HTMLElement>(
      `#shipment-expanded-detail-${shipment.identifier}`,
    );
    scrollIntoView(el, 'center');
  });
};

const onClear = () => {
  expandedRows.value = [];
  emit('clear');
};

const validate = async (): Promise<boolean> => {
  const promises = proposedShipmentIdComponents.value
    ?.filter(notNull)
    ?.map((s) => Promise.resolve(s.validate()));
  const resolvedPromises = await Promise.allSettled(promises);
  return resolvedPromises.every(
    (result) => result.status === 'fulfilled' && !!result.value,
  );
};

defineExpose({
  validate,
});
</script>

<style>
.route-builder-shipment-section.phase-1 {
  .section-header-right {
    height: var(--input-height);
  }

  .section-data {
    display: flex;
    flex-flow: column;
    gap: var(--gap-1);
    overflow: auto;
    padding-bottom: var(--sp-6);

    figure {
      display: flex;
      flex-flow: column;
      gap: var(--gap-1);
      justify-content: center;
      align-items: center;

      figcaption {
        font-weight: 700;
      }

      p {
        color: var(--p-gray-400);
      }
    }

    .data-table-container {
      .p-datatable {
        position: relative;
        height: 100%;
        overflow: hidden;

        .p-datatable-table-container {
          height: 100%;
          overflow: auto;

          .p-datatable-thead {
            position: sticky;
            top: 0;
          }

          .p-datatable-tbody {
            tr:has(.error) {
              background-color: var(--p-red-50);
            }
          }
        }
      }
    }
  }

  .section-footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--gap-1);
  }
}
</style>
